import {Socket} from "socket.io-client";
import {EventBus} from "./EventManager";
import {MediasoupManagerHandle} from "./MediasoupManager";
import {
  Direction,
  MediaState,
  OdysseyEvent,
  Participant,
  Position,
  RoomJoinedData,
} from "../types";

export interface RoomState {
  room: {id: string; participants: Map<string, Participant>} | null;
  localParticipant: Participant | null;
}

export interface RoomContext {
  socket: Socket;
  bus: EventBus;
  mediasoupManager: MediasoupManagerHandle;
  state: RoomState;
  emitEvent(event: OdysseyEvent, ...args: any[]): void;
}

export function createRoomActions(ctx: RoomContext) {
  const {socket, mediasoupManager, state, emitEvent} = ctx;

  async function joinRoom(data: {
    roomId: string;
    userId: string;
    deviceId: string;
    participantId?: string;
    position: Position;
    direction: Direction;
    bodyHeight?: string;
    bodyShape?: string;
    userName?: string;
    userEmail?: string;
  }): Promise<Participant> {
    // Wait for socket connection before joining — fail fast if auth is rejected
    if (!socket.connected) {
      await new Promise<void>((resolve, reject) => {
        const CONNECT_TIMEOUT_MS = 10_000;
        const timer = setTimeout(() => {
          socket.off("connect", onConnect);
          socket.off("connect_error", onError);
          reject(
            new Error(
              "[OdysseySDK] Connection timed out. Verify the server is reachable and your apiKey/userToken are valid."
            )
          );
        }, CONNECT_TIMEOUT_MS);

        const onConnect = () => {
          clearTimeout(timer);
          socket.off("connect_error", onError);
          resolve();
        };

        const onError = (err: Error) => {
          clearTimeout(timer);
          socket.off("connect", onConnect);
          reject(new Error(`[OdysseySDK] Connection failed: ${err.message}`));
        };

        socket.once("connect", onConnect);
        socket.once("connect_error", onError);
      });
    }

    return new Promise((resolve, reject) => {
      const handleRoomJoined = async (roomData: RoomJoinedData) => {
        try {
          socket.off("room-joined", handleRoomJoined);

          await mediasoupManager.loadDevice(roomData.routerRtpCapabilities);
          await mediasoupManager.createSendTransport(roomData.participantId);
          await mediasoupManager.createRecvTransport(roomData.participantId);
          mediasoupManager.sendDeviceRtpCapabilities(roomData.participantId);

          const localParticipantData = roomData.participants.find(
            (p) => p.participantId === roomData.participantId
          );
          if (!localParticipantData) {
            throw new Error("Could not find local participant in room data");
          }

          state.localParticipant = {
            ...localParticipantData,
            isLocal: true,
            producers: new Map(),
            consumers: new Map(),
            currentChannel: "spatial",
          } as Participant;

          state.room = {id: roomData.roomId, participants: new Map()};

          for (const pData of roomData.participants) {
            const participant: Participant = {
              ...pData,
              isLocal:
                pData.participantId === state.localParticipant.participantId,
              producers: new Map(),
              consumers: new Map(),
            };
            state.room.participants.set(pData.participantId, participant);
          }

          emitEvent("room-joined", roomData);
          resolve(state.localParticipant);
        } catch (error) {
          socket.off("room-joined", handleRoomJoined);
          reject(error);
        }
      };

      socket.on("room-joined", handleRoomJoined);
      socket.emit("join-room", data);
    });
  }

  function leaveRoom(): void {
    if (!socket.connected) return;
    socket.emit("leave-room");
    mediasoupManager.close();
    socket.disconnect();
    state.room = null;
    state.localParticipant = null;
    ctx.bus.removeAllListeners();
  }

  function updatePosition(
    position: Position,
    direction: Direction,
    spatialData?: {
      cameraDistance?: number;
      screenPos?: {x: number; y: number} | null;
      rot?: {x: number; y: number; z: number};
      pan?: number;
      dxLocal?: number;
    }
  ): void {
    if (state.localParticipant && state.room) {
      state.localParticipant.position = position;
      state.localParticipant.direction = direction;
      socket.emit("update-position", {
        participantId: state.localParticipant.participantId,
        conferenceId: state.room.id,
        roomId: state.room.id,
        position,
        direction,
        rot: spatialData?.rot,
        cameraDistance: spatialData?.cameraDistance,
        screenPos: spatialData?.screenPos,
        pan: spatialData?.pan,
        dxLocal: spatialData?.dxLocal,
      });
    }
  }

  function updateMediaState(mediaState: MediaState): void {
    if (state.localParticipant && state.room) {
      state.localParticipant.mediaState = mediaState;
      socket.emit("update-media-state", {
        participantId: state.localParticipant.participantId,
        conferenceId: state.room.id,
        roomId: state.room.id,
        mediaState,
      });
    }
  }

  return {joinRoom, leaveRoom, updatePosition, updateMediaState};
}
